/**
 * ArtAssets.jsx — Phase 6 Assets tab (2026-02).
 *
 * Grid view (5-column desktop) + List view (dense table). Filters +
 * search bar in the header. Card/row click opens the shared
 * AssetModal in edit mode.
 *
 * The `+ Add Asset` global button in ArtDepartmentPage's header is
 * wired via a module-level ref (window.__openArtAssetModal) so entry
 * points 2/3 (ArtByScene drawer, ArtByLocation block) can call the
 * SAME modal without prop-drilling through the whole department tree.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Search, Grid2X2, List as ListIcon } from 'lucide-react';
import { AuthImage } from '../../components/AuthImage';
import { useProjectRole } from '../../hooks/useProjectRole';
import { useAuth } from '../../context/AuthContext';
import {
  ASSET_STATUS_ORDER, ASSET_STATUS_TOKENS,
  ASSET_CATEGORIES, ASSET_CATEGORY_TOKENS,
  StatusPill,
} from './artShared';
import { useAssetActions } from './AssetActionsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AssetCard({ asset, onClick }) {
  const hero = asset.photos?.[0]?.storage_path;
  const catTok = ASSET_CATEGORY_TOKENS[asset.category] || ASSET_CATEGORY_TOKENS.scene_prop;
  return (
    <button type="button" onClick={() => onClick(asset)}
      className="text-left rounded-lg overflow-hidden border border-[var(--color-border)] bg-[var(--color-panel)] hover:border-[var(--color-accent)] transition"
      data-testid={`asset-card-${asset.id}`}>
      <div className="relative aspect-[4/3] bg-[var(--color-bg)]">
        {hero ? (
          <AuthImage path={hero} className="w-full h-full object-cover" alt="" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--color-text-mute)] text-2xl">📦</div>
        )}
        <div className="absolute top-2 right-2">
          <StatusPill status={asset.status} />
        </div>
        <div className="absolute top-2 left-2 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase"
             style={{ background: 'rgba(0,0,0,0.55)', color: catTok.fg }}>
          {catTok.label}
        </div>
      </div>
      <div className="p-2">
        <div className="text-sm font-bold text-[var(--color-text)] truncate">{asset.name}</div>
        <div className="text-[10px] text-[var(--color-text-dim)] mt-0.5 flex gap-2">
          <span>🎬 {(asset.linked_scene_ids || []).length}</span>
          <span>📍 {(asset.linked_location_ids || []).length}</span>
          {asset.cost && <span className="ml-auto text-[var(--color-text-dim)]">{asset.cost}</span>}
        </div>
        {(asset.tags || []).length > 0 && (
          <div className="flex gap-1 flex-wrap mt-1.5">
            {(asset.tags || []).slice(0, 3).map(t => (
              <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--color-panel)] border border-[var(--color-border)] text-[var(--color-text-dim)]">
                {t}
              </span>
            ))}
            {(asset.tags || []).length > 3 && (
              <span className="text-[9px] text-[var(--color-accent)]">+{asset.tags.length - 3}</span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

function AssetRow({ asset, onClick }) {
  const hero = asset.photos?.[0]?.storage_path;
  const catTok = ASSET_CATEGORY_TOKENS[asset.category] || ASSET_CATEGORY_TOKENS.scene_prop;
  return (
    <tr onClick={() => onClick(asset)}
        className="border-b border-[var(--color-border)] hover:bg-[var(--color-panel)] cursor-pointer"
        data-testid={`asset-row-${asset.id}`}>
      <td className="px-2 py-1.5">
        <div className="w-10 h-10 rounded overflow-hidden bg-[var(--color-bg)] border border-[var(--color-border)]">
          {hero ? <AuthImage path={hero} className="w-full h-full object-cover" alt="" />
                : <div className="w-full h-full flex items-center justify-center text-[var(--color-text-mute)]">📦</div>}
        </div>
      </td>
      <td className="px-2 py-1.5 text-sm text-[var(--color-text)]">{asset.name}</td>
      <td className="px-2 py-1.5 text-xs" style={{ color: catTok.fg }}>{catTok.label}</td>
      <td className="px-2 py-1.5"><StatusPill status={asset.status} /></td>
      <td className="px-2 py-1.5 text-xs text-[var(--color-text-sec)]">🎬 {(asset.linked_scene_ids || []).length}</td>
      <td className="px-2 py-1.5 text-xs text-[var(--color-text-sec)]">📍 {(asset.linked_location_ids || []).length}</td>
      <td className="px-2 py-1.5 text-xs text-[var(--color-text-sec)]">
        {(asset.tags || []).slice(0, 3).join(', ')}
      </td>
      <td className="px-2 py-1.5 text-xs text-[var(--color-text-dim)]">{asset.cost || '—'}</td>
    </tr>
  );
}

export default function ArtAssets({ projectId, token }) {
  const [view, setView] = useState(() => localStorage.getItem('art.assets.view') || 'grid');
  useEffect(() => { localStorage.setItem('art.assets.view', view); }, [view]);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem('art.assets.groupby') || 'none');
  useEffect(() => { localStorage.setItem('art.assets.groupby', groupBy); }, [groupBy]);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('art.assets.collapsed') || '[]')); }
    catch { return new Set(); }
  });
  useEffect(() => {
    localStorage.setItem('art.assets.collapsed', JSON.stringify([...collapsedGroups]));
  }, [collapsedGroups]);
  const toggleGroup = (key) => setCollapsedGroups(prev => {
    const nx = new Set(prev);
    if (nx.has(key)) nx.delete(key); else nx.add(key);
    return nx;
  });

  const [modalOpen, setModalOpen] = useState(false);   // legacy state kept for symmetry — modal now lives in context
  void modalOpen; void setModalOpen;

  const { permissions } = useProjectRole(projectId);
  const { user } = useAuth();
  // Art module (2026-07) — write affordances open to any authenticated user
  // with project access. Backend still enforces auth.
  const canWrite = !!user || !!permissions?.canWrite;

  const { openAssetModal } = useAssetActions();

  const authAxios = useMemo(() => axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (category) params.category = category;
      if (status) params.status = status;
      const r = await authAxios.get(`/projects/${projectId}/art/assets`, { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authAxios, projectId, search, category, status]);
  useEffect(() => { reload(); }, [reload]);

  // Load scene + location metadata for the modal (once) — the provider
  // handles this now; block kept as a no-op to preserve line stability
  // for grep in follow-up phases.
  useEffect(() => {}, [authAxios, projectId]);

  const openCreate = useCallback((prefill = null) => {
    openAssetModal({
      mode: 'create', prefill,
      onCreated: (saved) => onSaved(saved),
    });
  }, [openAssetModal]);   // eslint-disable-line react-hooks/exhaustive-deps
  const openEdit = useCallback((a) => {
    openAssetModal({
      mode: 'edit', asset: a,
      onCreated: (saved) => onSaved(saved),  // called on save-as-edit too
    });
  }, [openAssetModal]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Legacy compat: publishing this alias is now handled by the context
  // provider itself. This effect used to publish `window.__openArtAssetModal`
  // and is intentionally removed in Phase 8.

  const onSaved = (saved) => {
    setItems(prev => {
      const i = prev.findIndex(a => a.id === saved.id);
      if (i < 0) return [saved, ...prev];
      const nx = prev.slice();
      nx[i] = saved;
      return nx;
    });
    setTotal(t => t + 1);
  };
  const onDeleted = (deleted) => {
    setItems(prev => prev.filter(a => a.id !== deleted.id));
    setTotal(t => Math.max(0, t - 1));
  };
  void onDeleted;   // reserved for AssetActionsContext when it wires delete-callbacks

  const statusCounts = useMemo(() => {
    const c = {};
    for (const a of items) c[a.status] = (c[a.status] || 0) + 1;
    return c;
  }, [items]);

  return (
    <div className="p-4" data-testid="art-assets-view">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 border border-[var(--color-border)] rounded px-2 py-1 bg-[var(--color-bg)]">
          <Search className="w-3 h-3 text-[var(--color-text-dim)]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search assets…"
            className="bg-transparent border-0 h-6 text-xs w-56 outline-none text-[var(--color-text)]"
            data-testid="art-assets-search" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-xs h-8 px-2 text-[var(--color-text-sec)]"
          data-testid="art-assets-cat-filter">
          <option value="">All categories</option>
          {ASSET_CATEGORIES.map(k => (
            <option key={k} value={k}>{ASSET_CATEGORY_TOKENS[k].label}</option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-xs h-8 px-2 text-[var(--color-text-sec)]"
          data-testid="art-assets-status-filter">
          <option value="">All statuses</option>
          {ASSET_STATUS_ORDER.map(s => (
            <option key={s} value={s}>{ASSET_STATUS_TOKENS[s].label}</option>
          ))}
        </select>
        <div className="text-[10px] text-[var(--color-text-dim)] ml-2">
          {total} asset{total === 1 ? '' : 's'}
          {Object.keys(statusCounts).length > 0 && (
            <span className="ml-2 text-[var(--color-text-mute)]">
              ·{' '}{ASSET_STATUS_ORDER.filter(s => statusCounts[s]).map(s => `${statusCounts[s]} ${s}`).join(' · ')}
            </span>
          )}
        </div>
        <div className="ml-auto flex gap-2 items-center">
          <label className="text-[10px] text-[var(--color-text-dim)]">Group by</label>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
            className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-xs h-8 px-2 text-[var(--color-text-sec)]"
            data-testid="art-assets-groupby">
            <option value="none">— none —</option>
            <option value="status">Status</option>
            <option value="category">Category</option>
          </select>
          <div className="flex gap-1 border border-[var(--color-border)] rounded overflow-hidden">
            <button onClick={() => setView('grid')}
            className={`px-2 py-1 text-xs ${view === 'grid' ? 'bg-[var(--color-accent)] text-black' : 'bg-[var(--color-bg)] text-[var(--color-text-sec)]'}`}
            data-testid="art-assets-view-grid">
            <Grid2X2 className="w-3 h-3" />
          </button>
          <button onClick={() => setView('list')}
            className={`px-2 py-1 text-xs ${view === 'list' ? 'bg-[var(--color-accent)] text-black' : 'bg-[var(--color-bg)] text-[var(--color-text-sec)]'}`}
            data-testid="art-assets-view-list">
            <ListIcon className="w-3 h-3" />
          </button>
        </div>
        {canWrite && (
          <button onClick={() => openCreate(null)}
            className="text-xs px-3 py-1.5 rounded bg-[var(--color-accent)] text-black font-bold flex items-center gap-1"
            data-testid="art-assets-add-btn">
            <Plus className="w-3 h-3" /> Add Asset
          </button>
        )}
        </div>
      </div>

      {loading && <div className="text-center text-[var(--color-text-dim)] text-sm py-8">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-center text-[var(--color-text-dim)] text-sm py-12" data-testid="art-assets-empty">
          No assets yet.{canWrite && <> Click <b className="text-[var(--color-accent)]">+ Add Asset</b> to create one.</>}
        </div>
      )}

      {!loading && items.length > 0 && (() => {
        // Phase 12a — group-by rendering. If groupBy === 'none', the
        // single "all" bucket keeps the pre-existing flat render.
        let buckets;
        if (groupBy === 'status') {
          buckets = ASSET_STATUS_ORDER.map(s => ({
            key: `status-${s}`,
            label: (ASSET_STATUS_TOKENS[s]?.label || s) + ` (${items.filter(a => a.status === s).length})`,
            color: ASSET_STATUS_TOKENS[s]?.fg,
            items: items.filter(a => a.status === s),
          })).filter(b => b.items.length > 0);
        } else if (groupBy === 'category') {
          buckets = ASSET_CATEGORIES.map(c => ({
            key: `cat-${c}`,
            label: (ASSET_CATEGORY_TOKENS[c]?.label || c) + ` (${items.filter(a => a.category === c).length})`,
            color: ASSET_CATEGORY_TOKENS[c]?.fg,
            items: items.filter(a => a.category === c),
          })).filter(b => b.items.length > 0);
        } else {
          buckets = [{ key: 'all', label: null, items }];
        }
        return buckets.map(b => {
          const collapsed = collapsedGroups.has(b.key);
          return (
            <div key={b.key} className="mb-4" data-testid={`art-assets-group-${b.key}`}>
              {b.label && (
                <button type="button" onClick={() => toggleGroup(b.key)}
                  className="w-full text-left text-[10px] font-bold uppercase tracking-wider py-1 px-2 mb-2 rounded flex items-center gap-2"
                  style={{ color: b.color || 'var(--color-text-sec)', background: 'rgba(255,255,255,.03)' }}
                  data-testid={`art-assets-group-header-${b.key}`}>
                  <span>{collapsed ? '▶' : '▼'}</span>
                  <span>{b.label}</span>
                </button>
              )}
              {!collapsed && view === 'grid' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {b.items.map(a => <AssetCard key={a.id} asset={a} onClick={openEdit} />)}
                </div>
              )}
              {!collapsed && view === 'list' && (
                <table className="w-full text-sm border border-[var(--color-border)] rounded">
                  <thead>
                    <tr className="text-[10px] uppercase text-[var(--color-text-dim)] bg-[var(--color-bg)] border-b border-[var(--color-border)]">
                      <th className="px-2 py-1.5 text-left w-14">Photo</th>
                      <th className="px-2 py-1.5 text-left">Name</th>
                      <th className="px-2 py-1.5 text-left">Category</th>
                      <th className="px-2 py-1.5 text-left">Status</th>
                      <th className="px-2 py-1.5 text-left">Scenes</th>
                      <th className="px-2 py-1.5 text-left">Locs</th>
                      <th className="px-2 py-1.5 text-left">Tags</th>
                      <th className="px-2 py-1.5 text-left">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.items.map(a => <AssetRow key={a.id} asset={a} onClick={openEdit} />)}
                  </tbody>
                </table>
              )}
            </div>
          );
        });
      })()}
    </div>
  );
}
